import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getApiBase } from '../lib/api';

const API_BASE = getApiBase();
const NOAA_TIMEOUT_MS = 18000;
const NOAA_CACHE_TTL_MS = 10 * 60 * 1000;
const noaaCache = new Map();

const REGION_CONFIG = {
  
  neom: { lat: 28.08, lon: 34.95, name: 'Northern Region (Tabuk & NEOM)', keywords: ['neom', 'northern', 'tabuk'] },
  shushah: { lat: 27.93, lon: 34.88, name: 'Shushah Island', keywords: ['shushah'] },
  'al-wajh': { lat: 25.6, lon: 36.8, name: 'Al-Wajh & Umluj', keywords: ['wajh', 'umluj'] },
  thuwal: { lat: 22.3, lon: 38.9, name: 'Thuwal & Jeddah', keywords: ['thuwal', 'jeddah'] },
  southern: { lat: 16.88, lon: 41.58, name: 'Southern Region (Al-Lith to Jazan)', keywords: ['southern', 'jazan', 'lith'] },
};

export const alertLevelToHealth = (level) => {
  if (level == null || level <= 1) return 98;
  if (level <= 2) return 85;
  if (level <= 3) return 70;
  if (level <= 4) return 55;
  return 40;
};

export const getAlertBadge = (level) => {
  if (level == null || level <= 1) return { cls: 'healthy', text: 'Healthy' };
  if (level <= 3) return { cls: 'warning', text: 'Warning' };
  return { cls: 'critical', text: 'Critical' };
};

export const formatAlertLabel = (level, status) => {
  if (status) {
    const normalized = status.replace(/_/g, ' ').toLowerCase();
    if (normalized === 'normal') return 'NORMAL';
    if (normalized.includes('alert')) return normalized.toUpperCase();
    return normalized.toUpperCase();
  }
  if (level == null) return 'UNKNOWN';
  if (level <= 1) return 'NORMAL';
  if (level <= 3) return 'WARNING';
  return 'CRITICAL';
};

const nodeMatchesRegion = (node, regionKey) => {
  const cfg = REGION_CONFIG[regionKey];
  if (!cfg) return true;

  const regionText = (node.region || '').toLowerCase();
  if (cfg.keywords.some((k) => regionText.includes(k))) return true;

  const lat = node.latitude ?? node.Lan;
  const lon = node.longitude ?? node.Long;
  if (lat == null || lon == null) return false;

  return Math.abs(lat - cfg.lat) < 4 && Math.abs(lon - cfg.lon) < 4;
};

const getAlertSeverity = (alert) =>
  (alert.severity || alert.Severity || 'info').toLowerCase();

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '—';
  const mins = Math.round((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const formatLastSync = (dateStr) => {
  if (!dateStr) return 'Never';
  const mins = Math.round((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m`;
  return `${Math.round(mins / 60)}h`;
};

const buildHealthTrend = (nodes, alerts) => {
  const days = [];
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push({
      date: d,
      label: d.toLocaleDateString('en-US', { weekday: 'short' }),
    });
  }

  const baseHealth = nodes.length
    ? Math.round(nodes.reduce((sum, n) => sum + alertLevelToHealth(n.crw_alert_level), 0) / nodes.length)
    : 0;

  return days.map(({ date, label }) => {
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    const dayAlerts = (alerts || []).filter((a) => {
      const t = new Date(a.triggered_at || a.created_at);
      return t >= dayStart && t <= dayEnd;
    });

    const penalty = dayAlerts.length * 5;
    return { time: label, score: Math.max(0, Math.min(100, baseHealth - penalty)) };
  });
};

const fetchOpenMeteoMarine = async (lat, lon) => {
  const url = `https://marine-api.open-meteo.com/v1/marine?latitude=${lat}&longitude=${lon}&current=sea_surface_temperature,wave_height,wave_direction,wave_period`;
  const res = await fetch(url);
  const json = await res.json();
  if (json.error || json.reason) return null;
  return json;
};

const fetchOpenMeteoWeather = async (lat, lon) => {
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=wind_speed_10m,wind_direction_10m`;
  const res = await fetch(url);
  const json = await res.json();
  if (json.error || json.reason) return null;
  return json;
};

const buildEnvChart = async (lat, lon, dhw) => {
  try {
    const url = `https://marine-api.open-meteo.com/v1/marine?latitude=${lat}&longitude=${lon}&hourly=sea_surface_temperature&forecast_days=1&timezone=auto`;
    const res = await fetch(url);
    const json = await res.json();
    if (json.error || json.reason) return [];
    const times = json.hourly?.time || [];
    const temps = json.hourly?.sea_surface_temperature || [];
    const recent = times.slice(-6).map((t, i) => ({
      time: new Date(t).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
      temp: temps[temps.length - 6 + i] ?? null,
      stress: dhw ?? 0,
    }));
    return recent.filter((p) => p.temp != null);
  } catch {
    return [];
  }
};

const buildNodeGrowthChart = (nodes) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const year = new Date().getFullYear();
  const counts = monthNames.map((month, idx) => {
    const count = nodes.filter((n) => {
      if (!n.created_at) return false;
      const d = new Date(n.created_at);
      return d.getFullYear() === year && d.getMonth() === idx;
    }).length;
    return { month, nodes: count };
  });

  let cumulative = 0;
  return counts.map((row) => {
    cumulative += row.nodes;
    return { month: row.month, nodes: cumulative };
  });
};

const buildBleachingChart = (alerts) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const year = new Date().getFullYear();

  return monthNames.map((month, idx) => {
    const count = (alerts || []).filter((a) => {
      const t = new Date(a.triggered_at || a.created_at);
      if (Number.isNaN(t.getTime())) return false;
      const sev = getAlertSeverity(a);
      const isStress = sev === 'critical' || sev === 'high' || sev === 'warning' || sev === 'medium';
      return t.getFullYear() === year && t.getMonth() === idx && isStress;
    }).length;
    return { month, alerts: count };
  });
};

const pickLatestNode = (nodes) => {
  if (!nodes.length) return null;
  const sorted = [...nodes].sort((a, b) => {
    const aSync = a.crw_last_synced ? new Date(a.crw_last_synced).getTime() : 0;
    const bSync = b.crw_last_synced ? new Date(b.crw_last_synced).getTime() : 0;
    if (bSync !== aSync) return bSync - aSync;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
  return sorted[0];
};

const fetchNoaa = async (lat, lon) => {
  const key = `${lat.toFixed(2)},${lon.toFixed(2)}`;
  const cached = noaaCache.get(key);
  if (cached && Date.now() - cached.at < NOAA_CACHE_TTL_MS) {
    return cached.data;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), NOAA_TIMEOUT_MS);

  try {
    const res = await fetch(`${API_BASE}/api/noaa?lat=${lat}&lon=${lon}`, {
      signal: controller.signal,
    });
    if (res.ok) {
      const data = await res.json();
      noaaCache.set(key, { at: Date.now(), data });
      return data;
    }
  } catch (err) {
    if (err.name !== 'AbortError') console.error('NOAA fetch error:', err);
  } finally {
    clearTimeout(timer);
  }

  return { sst: null, sst_anomaly: null, dhw: null, alert_level: null, alert_status: null };
};

const mergeNoaaWithNodes = (noaa, nodes) => {
  const merged = { ...noaa };
  const syncedNodes = nodes.filter((n) => n.crw_sst != null);
  if (syncedNodes.length === 0) return merged;

  const avg = (key) => {
    const vals = syncedNodes.map((n) => n[key]).filter((v) => v != null);
    if (!vals.length) return null;
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    return Math.round(mean * 100) / 100;
  };

  merged.sst = merged.sst ?? avg('crw_sst');
  merged.sst_anomaly = merged.sst_anomaly ?? avg('crw_sst_anomaly');
  merged.dhw = merged.dhw ?? avg('crw_dhw');
  if (merged.alert_level == null) {
    merged.alert_level = Math.max(...syncedNodes.map((n) => n.crw_alert_level ?? 0));
  }
  return merged;
};

const buildNodeAlerts = (nodes) =>
  nodes
    .filter((node) => (node.crw_alert_level ?? 0) > 1)
    .map((node) => {
      const level = node.crw_alert_level ?? 0;
      const severity = level <= 3 ? 'warning' : 'critical';
      const triggeredAt = node.crw_last_synced || node.last_sync || node.updated_at || node.created_at;
      const stressParts = [
        node.crw_dhw != null ? `DHW ${node.crw_dhw}` : null,
        node.crw_sst != null ? `SST ${node.crw_sst}°C` : null,
        node.crw_sst_anomaly != null ? `Anomaly ${node.crw_sst_anomaly > 0 ? '+' : ''}${node.crw_sst_anomaly}°C` : null,
      ].filter(Boolean);

      return {
        id: `node-alert-${node.id}`,
        node_id: node.id,
        nodeName: node.name || `Node ${String(node.id || '').slice(0, 8)}`,
        severity,
        message: `${formatAlertLabel(level, node.alert_status)}${stressParts.length ? ` - ${stressParts.join(', ')}` : ''}`,
        triggered_at: triggeredAt,
        created_at: triggeredAt,
        timeAgo: formatTimeAgo(triggeredAt),
      };
    })
    .sort((a, b) => new Date(b.triggered_at || 0) - new Date(a.triggered_at || 0));

const computeRegionKpis = (nodes, allNodes) => {
  let healthyCount = 0;
  let warningCount = 0;
  let criticalCount = 0;
  nodes.forEach((node) => {
    const lvl = node.crw_alert_level;
    if (lvl == null || lvl <= 1) healthyCount += 1;
    else if (lvl <= 3) warningCount += 1;
    else criticalCount += 1;
  });

  const healthScores = nodes.map((n) => alertLevelToHealth(n.crw_alert_level));
  const avgHealth = healthScores.length
    ? Math.round(healthScores.reduce((a, b) => a + b, 0) / healthScores.length)
    : 0;

  const uniqueSites = new Set(
    nodes.map((node) => {
      const la = node.latitude ?? node.Lan;
      const lo = node.longitude ?? node.Long;
      return `${la},${lo}`;
    })
  ).size;

  const nodeAlerts = buildNodeAlerts(nodes);

  return {
    kpiData: {
      totalNodes: nodes.length,
      activeAlerts: nodeAlerts.length,
      avgHealth,
      sitesMonitored: uniqueSites,
      healthyCount,
      warningCount,
      criticalCount,
    },
    alerts: nodeAlerts.slice(0, 10),
    latestNode: pickLatestNode(nodes),
    chartData: {
      healthData: buildHealthTrend(nodes, nodeAlerts),
      nodeData: buildNodeGrowthChart(allNodes || []),
      bleachingData: buildBleachingChart(nodeAlerts),
    },
    bleachingEvents: nodes.filter((n) => (n.crw_alert_level ?? 0) >= 3).length,
    lastSynced: nodes.reduce((latest, n) => {
      if (!n.crw_last_synced) return latest;
      const t = new Date(n.crw_last_synced);
      return !latest || t > latest ? t : latest;
    }, null),
  };
};

export const useDashboardData = (region = 'thuwal') => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isEnvLoading, setIsEnvLoading] = useState(false);
  const baseCacheRef = useRef(null);
  const [baseReady, setBaseReady] = useState(false);
  const [latestNode, setLatestNode] = useState(null);
  const [kpiData, setKpiData] = useState({
    totalNodes: 0,
    activeAlerts: 0,
    avgHealth: 0,
    sitesMonitored: 0,
    healthyCount: 0,
    warningCount: 0,
    criticalCount: 0,
  });
  const [alerts, setAlerts] = useState([]);
  const [environmentData, setEnvironmentData] = useState({
    seaTemp: null,
    sstAnomaly: null,
    dhw: null,
    alertLevel: 'UNKNOWN',
    windSpeed: 0,
    windDirection: 0,
    waveHeight: 0,
    wavePeriod: 0,
    waveDirection: 0,
    currentLat: 0,
    currentLon: 0,
    regionName: '',
    lastSynced: null,
  });
  const [chartData, setChartData] = useState({
    healthData: [],
    envData: [],
    nodeData: [],
    bleachingData: [],
  });
  const [footerMetrics, setFooterMetrics] = useState({
    dataSources: 3,
    lastUpdated: '—',
    bleachingEvents: 0,
    operatorsOnline: 0,
  });

  const applyRegionFromCache = (regionKey, base) => {
    const nodes = (base.allNodes || []).filter((n) => nodeMatchesRegion(n, regionKey));
    const snapshot = computeRegionKpis(nodes, base.allNodes);

    setKpiData(snapshot.kpiData);
    setAlerts(snapshot.alerts);
    setLatestNode(snapshot.latestNode);
    setChartData((prev) => ({
      ...snapshot.chartData,
      envData: prev.envData,
    }));
    setFooterMetrics((prev) => ({
      ...prev,
      lastUpdated: formatLastSync(snapshot.lastSynced?.toISOString()),
      bleachingEvents: snapshot.bleachingEvents,
    }));

    return { nodes, regionCfg: REGION_CONFIG[regionKey] || REGION_CONFIG.thuwal };
  };

  const loadEnvironment = async (regionKey, nodes, regionCfg, operatorCount) => {
    const { lat, lon, name } = regionCfg;

    const [noaaRaw, weatherJson, marineJson, envChart] = await Promise.all([
      fetchNoaa(lat, lon),
      fetchOpenMeteoWeather(lat, lon),
      fetchOpenMeteoMarine(lat, lon),
      buildEnvChart(lat, lon, null),
    ]);

    let noaa = mergeNoaaWithNodes(noaaRaw, nodes);

    let windSpd = null;
    let windDir = null;
    let waveHgt = null;
    let wavePer = null;
    let waveDir = null;

    if (weatherJson?.current) {
      windSpd = weatherJson.current.wind_speed_10m ?? null;
      windDir = weatherJson.current.wind_direction_10m ?? null;
    }

    if (marineJson?.current) {
      const marineSst = marineJson.current.sea_surface_temperature;
      if (noaa.sst == null && marineSst != null) {
        noaa.sst = Math.round(marineSst * 10) / 10;
      }
      waveHgt = marineJson.current.wave_height ?? null;
      wavePer = marineJson.current.wave_period ?? null;
      waveDir = marineJson.current.wave_direction ?? null;
    }

    if (noaa.dhw == null && noaa.sst != null && noaa.sst >= 29) {
      noaa.alert_level = noaa.alert_level || 2;
      noaa.alert_status = noaa.alert_status || 'warning';
    }

    const lastSynced = nodes.reduce((latest, n) => {
      if (!n.crw_last_synced) return latest;
      const t = new Date(n.crw_last_synced);
      return !latest || t > latest ? t : latest;
    }, null);

    setEnvironmentData({
      seaTemp: noaa.sst,
      sstAnomaly: noaa.sst_anomaly,
      dhw: noaa.dhw,
      alertLevel: formatAlertLabel(noaa.alert_level, noaa.alert_status),
      windSpeed: windSpd != null ? Math.round(windSpd * 10) / 10 : null,
      windDirection: windDir != null ? Math.round(windDir) : null,
      waveHeight: waveHgt != null ? Math.round(waveHgt * 10) / 10 : null,
      wavePeriod: wavePer != null ? Math.round(wavePer * 10) / 10 : null,
      waveDirection: waveDir != null ? Math.round(waveDir) : null,
      currentLat: lat,
      currentLon: lon,
      regionName: name,
      lastSynced: lastSynced ? lastSynced.toISOString() : null,
    });

    setChartData((prev) => ({
      ...prev,
      envData: envChart.length ? envChart : [{ time: '—', temp: noaa.sst, stress: noaa.dhw ?? 0 }],
    }));

    setFooterMetrics((prev) => ({
      ...prev,
      lastUpdated: formatLastSync(lastSynced?.toISOString()),
      operatorsOnline: operatorCount ?? 0,
    }));
  };

  // Load backend data once per session
  useEffect(() => {
    let cancelled = false;

    const loadBase = async () => {
      setIsLoading(true);
      const token = localStorage.getItem('murjan_token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const [allNodes, operatorSummary] = await Promise.all([
          apiFetch('/api/nodes/'),
          apiFetch('/api/users/operator-count'),
        ]);

        if (cancelled) return;

        baseCacheRef.current = {
          allNodes: allNodes || [],
          operatorCount: operatorSummary?.count ?? 0,
        };

        setFooterMetrics((prev) => ({
          ...prev,
          operatorsOnline: operatorSummary?.count ?? 0,
        }));
        setBaseReady(true);
      } catch (error) {
        console.error('Dashboard load error:', error);
        const message = String(error?.message || '');
        if (
          message.toLowerCase().includes('authentication required') ||
          message.toLowerCase().includes('invalid authentication token') ||
          message.toLowerCase().includes('token has expired') ||
          message.toLowerCase().includes('user not found') ||
          message.toLowerCase().includes('deactivated')
        ) {
          localStorage.removeItem('murjan_token');
          localStorage.removeItem('murjan_user');
          navigate('/login');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadBase();
    return () => { cancelled = true; };
  }, [navigate]);

  // Region change: instant KPIs from cache + parallel env APIs (no full-page reload)
  useEffect(() => {
    if (!baseReady || !baseCacheRef.current) return;

    let cancelled = false;
    const base = baseCacheRef.current;
    const { nodes, regionCfg } = applyRegionFromCache(region, base);

    const run = async () => {
      setIsEnvLoading(true);
      await loadEnvironment(region, nodes, regionCfg, base.operatorCount);
      if (!cancelled) setIsEnvLoading(false);
    };

    run();
    return () => { cancelled = true; };
  }, [region, baseReady]);

  return {
    kpiData,
    alerts,
    environmentData,
    latestNode,
    chartData,
    footerMetrics,
    isLoading,
    isEnvLoading,
    getAlertBadge,
    alertLevelToHealth,
  };
};
